import { Component, Ref, Vue } from 'vue-property-decorator';
import DataTable, { TableVisibility } from '@/components/data-table/DataTable';
import { Options } from '@/components/mi-dialog/MiDialog';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import SupplierSizePackCreateOrUpdateDialog from '@/components/CustomDialog/SupplierSizePackCreateOrUpdateDialog/SupplierSizePackCreateOrUpdateDialog.vue';
import { SupplierSizePack } from '@/models/SupplierSizePack';
import { SupplierSize } from '@/models/SupplierSize';

@Component<SettingsSupplierSizePacks>({
  components: {
    SupplierSizePackCreateOrUpdateDialog,
  },
})
export default class SettingsSupplierSizePacks extends Vue {
  @Ref()
  protected supplierSizePacksDatatable!: DataTable;

  protected selectedSupplierSizePack: null | SupplierSizePack = null;

  protected isCreatingOrUpdatingSupplierSizePack: boolean = false;

  protected supplierSizePacksTableVisibility: TableVisibility = {
    title: false,
    checkboxes: false,
    search: true,
  }

  protected onIsCreatingSupplierSizePackInput(): void {
    this.selectedSupplierSizePack = null;
  }

  protected onSupplierSizePackCreated(): void {
    this.refreshSupplierSizePacksDatatable();
  }

  protected onSupplierSizePackUpdated(): void {
    this.refreshSupplierSizePacksDatatable();
  }

  protected refreshSupplierSizePacksDatatable(): void {
    if (! this.supplierSizePacksDatatable) { return; }

    this.supplierSizePacksDatatable.refresh(true);
  }

  protected async deleteSupplierSizePack(): Promise<null | SupplierSizePack> {
    if (!this.selectedSupplierSizePack) { return null; }

    const selectedSupplierSizePack = await this.selectedSupplierSizePack
      .delete()
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        return null;
      });

    return selectedSupplierSizePack;
  }

  protected get deleteSupplierSizePackDialogOptions(): Options {
    return {
      title: this.selectedSupplierSizePackName || 'Confirm deletion',
      text: 'Are you sure you wish to delete this supplier size?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Delete',
          action: async () => {
            const selectedSupplierSizePack = await this.deleteSupplierSizePack();

            if (! selectedSupplierSizePack) { return; }

            this.selectedSupplierSizePack = null;

            this.refreshSupplierSizePacksDatatable();
          },
        },
        cancel: {
          text: 'Cancel',
          action: () => (this.selectedSupplierSizePack = null),
        },
      },
    };
  }

  protected get selectedSupplierSizePackName(): string {
    return this.selectedSupplierSizePack
      ? this.selectedSupplierSizePack.name || ''
      : '';
  }

  protected get supplierSizePacksTableOptions(): Object {
    return {
      model: new SupplierSizePack()
        .include('sizes'),
      name: {
        singular: 'Supplier size pack',
        plural: 'Supplier size packs',
      },
      sort: {
        key: 'name',
        order: 'ASC',
      },
      headers: [
        {
          text: 'Name',
          value: 'name',
          sortable: {
            key: 'name',
            order: 'ASC',
          },
        },
        {
          text: 'Description',
          value: 'description',
          sortable: {
            key: 'description',
            order: 'ASC',
          },
        },
        {
          text: 'Supplier size(s)',
          value: 'sizes',
          sortable: {
            key: 'sizes',
            order: 'ASC',
          },
          transform: (supplierSizes: SupplierSize[]) => supplierSizes
            ? supplierSizes.map((supplierSize) => supplierSize.name).join(', ')
            : '',
        },
      ],
      actions: [
        {
          type: 'edit',
          label: 'edit',
          icon: 'edit',
          action: (datatable: DataTable, supplierSizePack: SupplierSizePack) => {
            this.selectedSupplierSizePack = supplierSizePack;

            this.isCreatingOrUpdatingSupplierSizePack = true;
          },
        },
        {
          type: 'delete',
          label: 'delete',
          icon: 'delete',
          action: (datatable: DataTable, supplierSizePack: SupplierSizePack) => {
            this.selectedSupplierSizePack = supplierSizePack;

            this.$store.dispatch('openDialog', this.deleteSupplierSizePackDialogOptions);
          },
        },
      ],
      add: () => (this.isCreatingOrUpdatingSupplierSizePack = true)
    };
  }
}
